/**
 * BLRRD paint engine — barrel exports + dab dispatch (Phase 4.C.paint.1,
 * Wave 9.1).
 *
 * Aggregates the paint sub-modules into one surface mounted on
 * `window.BlrrdPaint` (see src/pipeline/bootstrap.js). The legacy editor
 * (public/blrrd-editor-v15.js) drives strokes from its pointer event loop
 * using these primitives:
 *
 *   1. getBrushMask(cache, size, hardness)         → cached Gaussian mask
 *   2. startStroke({x,y,pressure,time})            → stroke state
 *   3. strokeTo(state, {x,y,pressure,time,spacing}, stamp)  per pointermove
 *   4. finishStroke(state, pad, w, h)              → dirty region for LOD
 *
 * The per-dab `stamp` callback the editor supplies dispatches to the right
 * op (stampBrushDab / stampPencilDab / stampEraserDab) — `dispatchDab`
 * below is the convenience router.
 *
 * Identity / baseline note: importing this module has NO side effects on
 * any render buffer. No dab lands until the editor explicitly calls a
 * stamp op during an active stroke, so the pixel-hash + worker-parity
 * baselines stay byte-identical when paint is loaded but unused.
 *
 * Provenance (clean-room): Hughes/van Dam §10.5, Porter-Duff 1984. NO
 * GIMP/GEGL/Krita/Photoshop source consulted.
 *
 * @module src/pipeline/paint/index
 */

import {
    buildBrushMask, getBrushMask, quantizeSize,
    HARDNESS_QUANT, MIN_DIAMETER, MAX_DIAMETER,
} from './brush-mask.js';
import {
    stampBrushDab, stampPencilDab, stampEraserDab,
    sampleMaskBilinear, worldToMaskCell, dabBounds, boundsToRegion, ALPHA_EPS,
} from './brush-ops.js';
import {
    resolvePressure, normalizedVelocity, effectiveSize, effectiveOpacity,
    VELOCITY_REFERENCE, SIZE_FLOOR, VELOCITY_OPACITY_RANGE,
} from './dynamics.js';
import {
    startStroke, strokeTo, finishStroke, dabSpacing,
    MIN_SPACING_PX, DEFAULT_SPACING_RATIO,
} from './paint-core.js';
import { stampCloneDab, bilinearSampleRGBA } from './clone.js';
import {
    stampSmudgeDab, createSmudgeAccum, primeSmudge, DEFAULT_SMUDGE_RATE,
} from './smudge.js';
import {
    stampDodgeBurnDab, tonalGate,
    DODGE_BURN_MAX_STRENGTH, LUMA_R, LUMA_G, LUMA_B, BAND_CENTRES, BAND_WIDTH,
} from './dodge-burn.js';
import {
    stampHealDab, solvePoissonChannel,
    DEFAULT_HEAL_ITERS, DEFAULT_HEAL_OMEGA, DEFAULT_HEAL_TOL, OMEGA_THRESHOLD,
} from './heal.js';

/** All paint tools. Wave 9.1: brush/pencil/eraser. Wave 9.2: clone/smudge/
 * dodge/burn. Wave 9.3: heal. */
export const PAINT_TOOLS = ['brush', 'pencil', 'eraser', 'clone', 'smudge', 'dodge', 'burn', 'heal'];

/**
 * Route a dab to the correct stamp op by tool name. Unknown tools no-op
 * (return null) so a bad UI state never throws mid-stroke.
 *
 * @param {string} tool          'brush' | 'pencil' | 'eraser'
 * @param {Float32Array} rgba    layer buffer
 * @param {number} w
 * @param {number} h
 * @param {number} cx
 * @param {number} cy
 * @param {object} brush         { mask, maskSize, effSize, color, flow }
 * @returns {{x:number,y:number,w:number,h:number}|null}
 */
export function dispatchDab(tool, rgba, w, h, cx, cy, brush) {
    switch (tool) {
        case 'brush':  return stampBrushDab(rgba, w, h, cx, cy, brush);
        case 'pencil': return stampPencilDab(rgba, w, h, cx, cy, brush);
        case 'eraser': return stampEraserDab(rgba, w, h, cx, cy, brush);
        case 'clone':  return stampCloneDab(rgba, w, h, cx, cy, brush);
        case 'smudge': return stampSmudgeDab(rgba, w, h, cx, cy, brush);
        case 'dodge':  return stampDodgeBurnDab(rgba, w, h, cx, cy, { ...brush, burn: false });
        case 'burn':   return stampDodgeBurnDab(rgba, w, h, cx, cy, { ...brush, burn: true });
        case 'heal':   return stampHealDab(rgba, w, h, cx, cy, brush);
        default:       return null;
    }
}

/** True when `tool` is a Wave-9.1 paint tool. */
export function isPaintTool(tool) {
    return PAINT_TOOLS.indexOf(tool) !== -1;
}

export {
    /* brush-mask */
    buildBrushMask, getBrushMask, quantizeSize,
    HARDNESS_QUANT, MIN_DIAMETER, MAX_DIAMETER,
    /* brush-ops */
    stampBrushDab, stampPencilDab, stampEraserDab,
    sampleMaskBilinear, worldToMaskCell, dabBounds, boundsToRegion, ALPHA_EPS,
    /* dynamics */
    resolvePressure, normalizedVelocity, effectiveSize, effectiveOpacity,
    VELOCITY_REFERENCE, SIZE_FLOOR, VELOCITY_OPACITY_RANGE,
    /* paint-core */
    startStroke, strokeTo, finishStroke, dabSpacing,
    MIN_SPACING_PX, DEFAULT_SPACING_RATIO,
    /* clone */
    stampCloneDab, bilinearSampleRGBA,
    /* smudge */
    stampSmudgeDab, createSmudgeAccum, primeSmudge, DEFAULT_SMUDGE_RATE,
    /* dodge/burn */
    stampDodgeBurnDab, tonalGate,
    DODGE_BURN_MAX_STRENGTH, LUMA_R, LUMA_G, LUMA_B, BAND_CENTRES, BAND_WIDTH,
    /* heal */
    stampHealDab, solvePoissonChannel,
    DEFAULT_HEAL_ITERS, DEFAULT_HEAL_OMEGA, DEFAULT_HEAL_TOL, OMEGA_THRESHOLD,
};
